import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  UserIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  FlagIcon,
  BellAlertIcon,
  PaintBrushIcon,
  EyeSlashIcon,
  LinkIcon,
  CreditCardIcon,
  QuestionMarkCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/solid";
import AskCouncilButton from "../components/AskCouncilButton";
import Navbar from "../components/Navbar";
import LinkedAccountsPanel from "./portfolio/LinkedAccounts";
import TierBadge from "../components/gating/TierBadge";
import { useUpgrade } from "../components/gating/UpgradeContext";
import { meetsFeature, featureMinTier } from "../config/tiers";
import { ThemeContext } from "../context/ThemeContext";
import { AuthContext } from "../context/AuthContext";
import {
  getMyProfile,
  updateMyProfile,
  changePassword,
  exportMyData,
  deleteMyAccount,
  listSessions,
  rotateShareSlug,
} from "../api/usersApi";
import { becomeAdvisor } from "../api/advisorApi";
import { listMySubscriptions, cancelSubscription } from "../api/marketplaceApi";

const SECTIONS = [
  { id: "account", label: "Account", icon: UserIcon },
  { id: "security", label: "Security", icon: ShieldCheckIcon },
  { id: "investing", label: "Investment profile", icon: ChartBarIcon },
  { id: "goals", label: "Goals", icon: FlagIcon },
  { id: "notifications", label: "Notifications", icon: BellAlertIcon },
  { id: "appearance", label: "Appearance", icon: PaintBrushIcon },
  { id: "privacy", label: "Privacy & data", icon: EyeSlashIcon },
  { id: "connected", label: "Connected accounts", icon: LinkIcon },
  { id: "billing", label: "Subscription", icon: CreditCardIcon },
  { id: "help", label: "Help & legal", icon: QuestionMarkCircleIcon },
];

const RISK_OPTIONS = [
  { value: "conservative", label: "Conservative", desc: "Capital preservation, steady returns" },
  { value: "moderate", label: "Moderate", desc: "Balanced growth and stability" },
  { value: "aggressive", label: "Aggressive", desc: "Growth-first, higher volatility" },
];

const STYLE_OPTIONS = [
  { value: "", label: "Not set" },
  { value: "value", label: "Value", desc: "Buy underpriced fundamentals, hold long" },
  { value: "growth", label: "Growth", desc: "Chase earnings/revenue acceleration" },
  { value: "blend", label: "Blend", desc: "Mix of value and growth" },
  { value: "income", label: "Income", desc: "Bonds, dividends, REITs — yield-first" },
  { value: "dividend", label: "Dividend", desc: "Equity-only but skewed to payers" },
  { value: "passive_index", label: "Passive index", desc: "ETFs only, no stock picking" },
];

const LIQUIDITY_OPTIONS = [
  { value: "", label: "Not set" },
  { value: "low", label: "Low — long lockups OK" },
  { value: "medium", label: "Medium — could need some out in 1-2 years" },
  { value: "high", label: "High — must stay easy to liquidate" },
];

const SECTOR_EXCLUSIONS = [
  "Tobacco",
  "Firearms",
  "Fossil fuels",
  "Gambling",
  "Alcohol",
  "Adult entertainment",
];

const TAX_JURISDICTIONS = [
  { value: "", label: "Not set" },
  { value: "IN", label: "🇮🇳 India (STCG/LTCG, 80C)" },
  { value: "US", label: "🇺🇸 United States (cap gains, IRA)" },
  { value: "GB", label: "🇬🇧 United Kingdom" },
  { value: "CA", label: "🇨🇦 Canada" },
  { value: "AU", label: "🇦🇺 Australia" },
  { value: "SG", label: "🇸🇬 Singapore" },
  { value: "AE", label: "🇦🇪 UAE" },
];

const CURRENCIES = ["USD", "INR", "EUR", "GBP", "AED", "SGD", "AUD", "CAD", "JPY", "HKD"];
const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "hi", label: "हिन्दी (Hindi)" },
  { value: "es", label: "Español" },
  { value: "fr", label: "Français" },
  { value: "de", label: "Deutsch" },
  { value: "ja", label: "日本語" },
];

const NOTIF_CATEGORIES = [
  { key: "daily_brief", label: "Daily brief", desc: "Morning portfolio brief from Compass" },
  { key: "alerts", label: "Portfolio alerts", desc: "Concentration risk, drawdown, volatility spikes" },
  { key: "security", label: "Security", desc: "Sign-ins, password changes, 2FA events" },
  { key: "social", label: "Friends & community", desc: "Friend requests, accepted, mentions" },
  { key: "marketing", label: "Product updates", desc: "New features, occasional newsletters" },
];
const NOTIF_CHANNELS = ["email", "push", "sms"];

const DEFAULT_NOTIF_PREFS = (() => {
  const out = { quiet_hours: { start: "22:00", end: "07:00" }, brief_time: "07:00" };
  for (const c of NOTIF_CATEGORIES) {
    out[c.key] = { email: c.key !== "marketing", push: c.key !== "marketing", sms: false };
  }
  return out;
})();

const SectionShell = ({ title, description, children, footer }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold">{title}</h2>
      {description && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{description}</p>
      )}
    </div>
    <div className="space-y-6">{children}</div>
    {footer}
  </div>
);

const Card = ({ children, className = "" }) => (
  <div
    className={`p-5 sm:p-6 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}
  >
    {children}
  </div>
);

const Field = ({ label, hint, children }) => (
  <label className="block">
    <div className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1.5">
      {label}
    </div>
    {children}
    {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className={`w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500 ${props.className || ""}`}
  />
);

const Select = ({ children, ...props }) => (
  <select
    {...props}
    className={`w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500 ${props.className || ""}`}
  >
    {children}
  </select>
);

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange?.(!checked)}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
      checked ? "bg-indigo-600" : "bg-gray-400/40"
    } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    aria-pressed={!!checked}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

const StatusBanner = ({ kind, children, onClose }) => {
  const cls =
    kind === "error"
      ? "bg-red-500/10 text-red-500 border-red-500/40"
      : "bg-emerald-500/10 text-emerald-500 border-emerald-500/40";
  const Icon = kind === "error" ? ExclamationTriangleIcon : CheckCircleIcon;
  return (
    <div className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${cls}`}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex-1">{children}</div>
      {onClose && (
        <button onClick={onClose} className="text-current opacity-70 hover:opacity-100">
          <XMarkIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

const ShareSection = ({ profile, setProfile, patch, flash }) => {
  const { promptUpgrade } = useUpgrade();
  const shareLocked = !meetsFeature(profile?.tier, "public_share");
  const [generating, setGenerating] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const slug = profile.share_slug || "";
  const enabled = !!profile.share_enabled;
  const shareUrl = slug
    ? `${window.location.origin}/p/${slug}`
    : "";

  const ensureSlug = async () => {
    if (slug) return slug;
    setGenerating(true);
    try {
      const res = await rotateShareSlug();
      const next = { ...profile, share_slug: res.share_slug };
      setProfile(next);
      return res.share_slug;
    } catch {
      flash("error", "Could not generate share link.");
      return "";
    } finally {
      setGenerating(false);
    }
  };

  const onToggle = async (next) => {
    // Public share is a Pro feature — prompt to upgrade instead of enabling.
    if (next && shareLocked) {
      promptUpgrade("public_share");
      return;
    }
    if (next && !slug) {
      const newSlug = await ensureSlug();
      if (!newSlug) return;
    }
    setProfile({ ...profile, share_enabled: next });
    patch({ share_enabled: next });
  };

  const onRotate = async () => {
    if (!window.confirm("Rotate the share link? Anyone with the old link will get a 404.")) return;
    setGenerating(true);
    try {
      const res = await rotateShareSlug();
      setProfile({ ...profile, share_slug: res.share_slug });
      flash("success", "New link generated.");
    } catch {
      flash("error", "Could not rotate.");
    } finally {
      setGenerating(false);
    }
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // best-effort
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <h3 className="font-bold flex items-center gap-2">
            Public portfolio share
            {shareLocked && <TierBadge tier={featureMinTier("public_share")} />}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Generate a shareable link to a sanitised view of your portfolio —
            return %, asset-class allocation, latest brief headline. Holdings
            and amounts are hidden by default.
          </p>
        </div>
        <Toggle checked={enabled} onChange={onToggle} disabled={generating} />
      </div>

      {enabled ? (
        <>
          <div className="mt-2 mb-3 flex items-start justify-between gap-3 p-3 rounded-lg border border-gray-200 dark:border-white/10">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">Show actual amounts</p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Reveal portfolio value + return amount in your display currency.
                Off = percentages only.
              </p>
            </div>
            <Toggle
              checked={!!profile.share_show_amounts}
              onChange={(v) => {
                setProfile({ ...profile, share_show_amounts: v });
                patch({ share_show_amounts: v });
              }}
            />
          </div>

          <div className="mb-3 flex items-start justify-between gap-3 p-3 rounded-lg border border-gray-200 dark:border-white/10">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">Show today's Compass brief headline</p>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Off by default — briefs can mention specific holdings and amounts.
                Only turn this on if you've checked today's brief and you're happy
                for strangers to see its title + first bullet.
              </p>
            </div>
            <Toggle
              checked={!!profile.share_show_brief}
              onChange={(v) => {
                setProfile({ ...profile, share_show_brief: v });
                patch({ share_show_brief: v });
              }}
            />
          </div>

          <Field label="Public bio (optional)" hint="Shown above your numbers">
            <Input
              value={profile.share_bio || ""}
              onChange={(e) =>
                setProfile({ ...profile, share_bio: e.target.value })
              }
              onBlur={() => patch({ share_bio: profile.share_bio || "" })}
              placeholder='e.g. "Long-term value investor. India + US."'
              maxLength={200}
            />
          </Field>

          <div className="mt-3 p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/30">
            <p className="text-[10px] uppercase font-bold tracking-widest text-indigo-500 mb-1">
              Your share link
            </p>
            <div className="flex items-center gap-2">
              <input
                value={shareUrl}
                readOnly
                onClick={(e) => e.target.select()}
                className="flex-1 px-3 py-2 rounded-md bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-700 text-sm font-mono"
              />
              <button
                onClick={onCopy}
                className="px-3 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold whitespace-nowrap"
              >
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
            <button
              onClick={onRotate}
              disabled={generating}
              className="mt-2 text-[11px] font-semibold text-gray-500 hover:text-red-500"
            >
              Rotate link (invalidates the old one)
            </button>
          </div>
        </>
      ) : (
        <p className="text-[11px] text-gray-500 mt-2">
          When enabled, anyone with the link can see a read-only public view.
          Holdings list and amounts stay private unless you explicitly opt in.
        </p>
      )}
    </div>
  );
};

const newGoalId = () =>
  `g_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

const GoalsEditor = ({ profile, setProfile, patch }) => {
  const goals = Array.isArray(profile.goals) ? profile.goals : [];

  const update = (next) => {
    setProfile({ ...profile, goals: next });
    patch({ goals: next });
  };

  const onAdd = () => {
    update([
      ...goals,
      {
        id: newGoalId(),
        label: "",
        target_amount: "",
        target_currency: profile.display_currency || "USD",
        target_date: "",
        contribution_monthly: "",
        priority: "medium",
      },
    ]);
  };

  const onChange = (index, key, value) => {
    const next = goals.map((g, i) => (i === index ? { ...g, [key]: value } : g));
    setProfile({ ...profile, goals: next });
  };

  const commit = () => patch({ goals });

  const onRemove = (index) => {
    const next = goals.filter((_, i) => i !== index);
    update(next);
  };

  if (goals.length === 0) {
    return (
      <div className="text-center py-10">
        <FlagIcon className="w-10 h-10 text-indigo-500 mx-auto mb-3" />
        <h3 className="font-bold mb-1">No goals yet</h3>
        <p className="text-sm text-gray-500 mb-5">
          Add a goal — Compass will project your portfolio forward and tell you whether
          you'll get there.
        </p>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold"
        >
          <PlusIcon className="w-4 h-4" />
          Add your first goal
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {goals.map((g, i) => {
        const askPrefill = g.label?.trim()
          ? `Am I on track for my goal "${g.label}"${
              g.target_amount ? ` (${g.target_currency || "USD"} ${g.target_amount})` : ""
            }${g.target_date ? ` by ${g.target_date}` : ""}? What should I do to close any gap?`
          : null;
        return (
          <div
            key={g.id || i}
            className="p-4 rounded-xl border border-gray-200 dark:border-white/10 space-y-3"
          >
            <div className="flex items-start gap-3">
              <input
                value={g.label || ""}
                onChange={(e) => onChange(i, "label", e.target.value)}
                onBlur={commit}
                placeholder='e.g. "Buy a house in Hyderabad"'
                className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500 font-semibold"
              />
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="w-9 h-9 rounded-lg flex items-center justify-center text-red-500 hover:bg-red-500/10"
                title="Remove goal"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">
                  Target amount
                </p>
                <input
                  inputMode="decimal"
                  value={g.target_amount ?? ""}
                  onChange={(e) => onChange(i, "target_amount", e.target.value)}
                  onBlur={commit}
                  placeholder="500000"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">
                  Currency
                </p>
                <select
                  value={g.target_currency || "USD"}
                  onChange={(e) => onChange(i, "target_currency", e.target.value)}
                  onBlur={commit}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                >
                  {CURRENCIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">
                  Target date
                </p>
                <input
                  type="date"
                  value={g.target_date || ""}
                  onChange={(e) => onChange(i, "target_date", e.target.value)}
                  onBlur={commit}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">
                  Monthly contribution (optional)
                </p>
                <input
                  inputMode="decimal"
                  value={g.contribution_monthly ?? ""}
                  onChange={(e) => onChange(i, "contribution_monthly", e.target.value)}
                  onBlur={commit}
                  placeholder="2000"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">
                  Priority
                </p>
                <select
                  value={g.priority || "medium"}
                  onChange={(e) => onChange(i, "priority", e.target.value)}
                  onBlur={commit}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                >
                  <option value="high">High — must hit</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low — nice to have</option>
                </select>
              </div>
            </div>
            {askPrefill && (
              <div className="pt-2">
                <AskCouncilButton
                  label="Am I on track?"
                  prefill={askPrefill}
                />
              </div>
            )}
          </div>
        );
      })}
      <button
        type="button"
        onClick={onAdd}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-indigo-500/40 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-sm font-semibold hover:bg-indigo-500/20"
      >
        <PlusIcon className="w-4 h-4" />
        Add another goal
      </button>
    </div>
  );
};


// Advisor-marketplace onboarding modal — Phase 1.
// Just collects display_name + jurisdiction; the rest of the profile
// (bio, headshot, video, qualifications) is filled in later from the
// advisor dashboard which lands in Phase 2.
const BecomeAdvisorModal = ({ open, onClose, onSuccess }) => {
  const [displayName, setDisplayName] = React.useState("");
  const [jurisdiction, setJurisdiction] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState("");

  React.useEffect(() => {
    if (!open) {
      setDisplayName("");
      setJurisdiction("");
      setErrorMsg("");
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!displayName.trim()) {
      setErrorMsg("Display name is required.");
      return;
    }
    setSubmitting(true);
    setErrorMsg("");
    try {
      const advisor = await becomeAdvisor({
        display_name: displayName.trim(),
        jurisdiction,
      });
      onSuccess(advisor);
    } catch (err) {
      const data = err?.response?.data || {};
      const firstKey = Object.keys(data)[0];
      const msg = firstKey
        ? `${firstKey}: ${Array.isArray(data[firstKey]) ? data[firstKey][0] : data[firstKey]}`
        : "Could not submit. Try again.";
      setErrorMsg(msg);
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md p-6 rounded-2xl bg-white dark:bg-[#0f1624] border border-gray-200 dark:border-white/10 shadow-2xl"
      >
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-xl font-bold">Apply to be an advisor</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-white/5"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
          Start with the basics. You'll add a bio, qualifications, intro video,
          and your first strategy from the advisor dashboard once your account
          is set up.
        </p>

        <Field label="Display name" hint="Shown publicly on your advisor profile.">
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Priya Capital Research"
            maxLength={120}
            autoFocus
          />
        </Field>

        <div className="mt-4">
          <Field label="Jurisdiction" hint="Where you're licensed / based. Used for compliance disclaimers.">
            <Select
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.target.value)}
            >
              {TAX_JURISDICTIONS.map((j) => (
                <option key={j.value || "_blank"} value={j.value}>
                  {j.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {errorMsg && (
          <div className="mt-4 text-sm text-red-500 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
            {errorMsg}
          </div>
        )}

        <div className="mt-6 flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-white/15 text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Apply"}
          </button>
        </div>
      </form>
    </div>
  );
};

// Advisor-marketplace subscriptions the user is actively paying / following.
// Sits inside the Subscription tab as a stacked list — explicitly designed
// for the common "subscribed to multiple advisors" case.
const formatSubPrice = (minor, currency, interval) => {
  if (!Number.isFinite(minor)) return `${currency || ""}/${interval || ""}`;
  return `${currency || "USD"} ${(minor / 100).toFixed(2)} / ${interval || "month"}`;
};

const MySubscriptionsPanel = () => {
  const [subs, setSubs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busyId, setBusyId] = React.useState(null);

  const load = async () => {
    try {
      setSubs(await listMySubscriptions());
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
  }, []);

  const onCancel = async (sub) => {
    if (!window.confirm(`Cancel your subscription to "${sub.plan_name}"?`)) return;
    setBusyId(sub.id);
    try {
      await cancelSubscription(sub.id);
      load();
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <p className="text-sm text-gray-500">Loading your subscriptions…</p>
      </Card>
    );
  }
  if (subs.length === 0) {
    return (
      <Card>
        <h3 className="font-bold mb-1">My subscriptions</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          You aren't subscribed to any advisor plans yet.{" "}
          <Link to="/marketplace" className="text-indigo-500 hover:underline">
            Browse the marketplace →
          </Link>
        </p>
      </Card>
    );
  }
  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold">My subscriptions</h3>
        <Link to="/marketplace" className="text-xs font-semibold text-indigo-500 hover:underline">
          Browse marketplace →
        </Link>
      </div>
      <div className="space-y-3">
        {subs.map((s) => (
          <div
            key={s.id}
            className="p-4 rounded-xl border border-gray-200 dark:border-white/10 bg-white/40 dark:bg-white/[0.02]"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="font-semibold">
                  <Link
                    to={`/marketplace/advisor/${s.advisor_slug}`}
                    className="hover:underline"
                  >
                    {s.advisor_display_name}
                  </Link>
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{s.plan_name}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Status: <span className="font-semibold">{s.status}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => onCancel(s)}
                disabled={busyId === s.id}
                className="px-3 py-1.5 rounded-lg text-red-500 hover:bg-red-500/10 text-sm font-medium disabled:opacity-50"
              >
                {busyId === s.id ? "Canceling…" : "Cancel"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

const Settings = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme, setTheme } = useContext(ThemeContext);
  const { refreshProfile } = useContext(AuthContext);
  const { promptUpgrade } = useUpgrade();
  const initialTab = useMemo(() => {
    const requested = searchParams.get("tab");
    return SECTIONS.some((s) => s.id === requested) ? requested : "account";
  }, [searchParams]);
  const [active, setActive] = useState(initialTab);

  // Keep the URL ?tab=... in sync with the active section so deep links
  // (from Profile completeness chips) land you on the right pane.
  useEffect(() => {
    if (searchParams.get("tab") !== active) {
      setSearchParams({ tab: active }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);

  const [sessions, setSessions] = useState([]);

  const [advisorModalOpen, setAdvisorModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const p = await getMyProfile();
        setProfile(p);
      } catch {
        navigate("/login");
        return;
      } finally {
        setLoading(false);
      }
      try {
        const s = await listSessions();
        setSessions(s.sessions || []);
      } catch {
        setSessions([]);
      }
    })();
  }, [navigate]);

  const notifPrefs = useMemo(
    () => ({ ...DEFAULT_NOTIF_PREFS, ...(profile?.notification_prefs || {}) }),
    [profile],
  );
  const appearancePrefs = useMemo(
    () => ({
      density: "comfortable",
      default_landing: "/portfolio",
      ...(profile?.appearance_prefs || {}),
    }),
    [profile],
  );

  const flash = (kind, message) => {
    setStatus({ kind, message });
    setTimeout(() => setStatus(null), 4000);
  };

  const patch = async (payload) => {
    try {
      const updated = await updateMyProfile(payload);
      setProfile(updated);
      flash("success", "Saved.");
    } catch (err) {
      const data = err.response?.data || {};
      const firstKey = Object.keys(data)[0];
      const msg = firstKey
        ? `${firstKey}: ${Array.isArray(data[firstKey]) ? data[firstKey][0] : data[firstKey]}`
        : "Could not save.";
      flash("error", msg);
    }
  };

  const updateNotif = (cat, ch, value) => {
    const next = { ...notifPrefs, [cat]: { ...(notifPrefs[cat] || {}), [ch]: value } };
    patch({ notification_prefs: next });
  };

  const updateNotifMeta = (key, value) => {
    const next = { ...notifPrefs, [key]: value };
    patch({ notification_prefs: next });
  };

  const updateAppearance = (key, value) => {
    const next = { ...appearancePrefs, [key]: value };
    patch({ appearance_prefs: next });
  };

  const onSubmitPassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) {
      flash("error", "New passwords don't match.");
      return;
    }
    setPwdSaving(true);
    try {
      await changePassword({
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      flash("success", "Password updated.");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      const data = err.response?.data || {};
      const firstKey = Object.keys(data)[0];
      const msg = firstKey
        ? Array.isArray(data[firstKey])
          ? data[firstKey][0]
          : data[firstKey]
        : "Could not update password.";
      flash("error", String(msg));
    } finally {
      setPwdSaving(false);
    }
  };

  const onExport = async () => {
    // Data export is a Pro feature.
    if (!meetsFeature(profile?.tier, "data_export")) {
      promptUpgrade("data_export");
      return;
    }
    try {
      const snapshot = await exportMyData();
      const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `finai-export-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      flash("success", "Export downloaded.");
    } catch {
      flash("error", "Export failed.");
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Permanently delete your account? This cannot be undone.")) return;
    try {
      await deleteMyAccount();
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      navigate("/");
    } catch {
      flash("error", "Could not delete account.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gradient-to-b dark:from-gray-900 dark:via-gray-950 dark:to-black dark:text-white">
      <Navbar />

      <div className="max-w-6xl mx-auto pt-28 px-4 sm:px-6 pb-20">
        <div className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-bold">Settings</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Tune how FinAI works for you.
          </p>
        </div>

        {status && (
          <div className="mb-4">
            <StatusBanner kind={status.kind} onClose={() => setStatus(null)}>
              {status.message}
            </StatusBanner>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
          <aside>
            <nav className="lg:sticky lg:top-24 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                const isActive = active === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setActive(s.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition ${
                      isActive
                        ? "bg-indigo-600 text-white"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {s.label}
                  </button>
                );
              })}
            </nav>
          </aside>

          <main className="min-w-0 space-y-6">
            {active === "account" && (
              <SectionShell title="Account" description="Your identity on FinAI.">
                <Card>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Username">
                      <Input
                        value={profile.username || ""}
                        onChange={(e) => setProfile({ ...profile, username: e.target.value })}
                        onBlur={() => patch({ username: profile.username })}
                      />
                    </Field>
                    <Field
                      label="Email"
                      hint={profile.email_verified ? "Verified" : "Not verified"}
                    >
                      <Input
                        type="email"
                        value={profile.email || ""}
                        onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                        onBlur={() => patch({ email: profile.email })}
                      />
                    </Field>
                    <Field label="First name">
                      <Input
                        value={profile.first_name || ""}
                        onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                        onBlur={() => patch({ first_name: profile.first_name || "" })}
                      />
                    </Field>
                    <Field label="Last name">
                      <Input
                        value={profile.last_name || ""}
                        onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                        onBlur={() => patch({ last_name: profile.last_name || "" })}
                      />
                    </Field>
                    <Field label="Phone" hint="Used for SMS alerts (optional)">
                      <Input
                        type="tel"
                        value={profile.phone || ""}
                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                        onBlur={() => patch({ phone: profile.phone || "" })}
                      />
                    </Field>
                    <Field label="Country (ISO-2)">
                      <Input
                        maxLength={2}
                        value={profile.country || ""}
                        onChange={(e) =>
                          setProfile({
                            ...profile,
                            country: e.target.value.toUpperCase().slice(0, 2),
                          })
                        }
                        onBlur={() => patch({ country: profile.country || "" })}
                      />
                    </Field>
                    <Field label="Timezone">
                      <Input
                        value={profile.timezone || ""}
                        onChange={(e) => setProfile({ ...profile, timezone: e.target.value })}
                        onBlur={() => patch({ timezone: profile.timezone || "UTC" })}
                        placeholder="Asia/Kolkata"
                      />
                    </Field>
                    <Field label="Language">
                      <Select
                        value={profile.language || "en"}
                        onChange={(e) => {
                          setProfile({ ...profile, language: e.target.value });
                          patch({ language: e.target.value });
                        }}
                      >
                        {LANGUAGES.map((l) => (
                          <option key={l.value} value={l.value}>
                            {l.label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field
                      label="Display currency"
                      hint="How values are shown across the app"
                    >
                      <Select
                        value={profile.display_currency || "USD"}
                        onChange={(e) => {
                          setProfile({ ...profile, display_currency: e.target.value });
                          patch({ display_currency: e.target.value });
                        }}
                      >
                        {CURRENCIES.map((c) => (
                          <option key={c}>{c}</option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="Bio">
                      <Input
                        value={profile.bio || ""}
                        onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                        onBlur={() => patch({ bio: profile.bio || "" })}
                        placeholder="What kind of investor are you?"
                      />
                    </Field>
                  </div>
                </Card>

                <Card className="border-red-500/30">
                  <h3 className="text-base font-bold text-red-500 mb-1">Danger zone</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Permanently delete your account. This cannot be undone.
                  </p>
                  <button
                    onClick={onDelete}
                    className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-semibold"
                  >
                    Delete my account
                  </button>
                </Card>
              </SectionShell>
            )}

            {active === "security" && (
              <SectionShell title="Security" description="Protect your account.">
                <Card>
                  <h3 className="font-bold mb-3">Change password</h3>
                  <form
                    onSubmit={onSubmitPassword}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                  >
                    <Field label="Current password">
                      <Input
                        type="password"
                        autoComplete="current-password"
                        value={pwd.current_password}
                        onChange={(e) =>
                          setPwd({ ...pwd, current_password: e.target.value })
                        }
                      />
                    </Field>
                    <div className="hidden sm:block" />
                    <Field label="New password">
                      <Input
                        type="password"
                        autoComplete="new-password"
                        value={pwd.new_password}
                        onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
                      />
                    </Field>
                    <Field label="Confirm new password">
                      <Input
                        type="password"
                        autoComplete="new-password"
                        value={pwd.confirm}
                        onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                      />
                    </Field>
                    <div className="sm:col-span-2 flex justify-end">
                      <button
                        type="submit"
                        disabled={pwdSaving}
                        className={`px-4 py-2 rounded-lg text-white font-semibold ${
                          pwdSaving ? "bg-indigo-400" : "bg-indigo-600 hover:bg-indigo-700"
                        }`}
                      >
                        {pwdSaving ? "Updating..." : "Update password"}
                      </button>
                    </div>
                  </form>
                </Card>

                <Card>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-bold">Two-factor authentication</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Adds a one-time code from your authenticator app on every sign-in.
                      </p>
                      <span className="inline-block mt-2 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-500">
                        Coming soon
                      </span>
                    </div>
                    <Toggle checked={profile.two_factor_enabled} disabled />
                  </div>
                </Card>

                <Card>
                  <h3 className="font-bold mb-3">Active sessions</h3>
                  {sessions.length === 0 ? (
                    <p className="text-sm text-gray-500">No sessions to show.</p>
                  ) : (
                    <ul className="space-y-2">
                      {sessions.map((s) => (
                        <li
                          key={s.id}
                          className="flex items-center justify-between p-3 rounded-lg bg-gray-100 dark:bg-white/5 text-sm"
                        >
                          <div className="min-w-0">
                            <p className="font-semibold truncate">
                              {s.current ? "This device" : s.device}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {s.ip} · last active{" "}
                              {new Date(s.last_active).toLocaleString()}
                            </p>
                          </div>
                          {s.current && (
                            <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500">
                              Current
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-[11px] text-gray-500 mt-3">
                    Full session list and remote-revoke is coming soon.
                  </p>
                </Card>
              </SectionShell>
            )}

            {active === "investing" && (
              <SectionShell
                title="Investment profile"
                description="The Council uses these to tailor advice. The Tally agent uses your tax jurisdiction to apply the right tax rules."
              >
                <Card>
                  <Field label="Risk tolerance">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {RISK_OPTIONS.map((r) => {
                        const active = profile.risk_tolerance === r.value;
                        return (
                          <button
                            key={r.value}
                            onClick={() => {
                              setProfile({ ...profile, risk_tolerance: r.value });
                              patch({ risk_tolerance: r.value });
                            }}
                            className={`text-left p-3 rounded-lg border transition ${
                              active
                                ? "border-indigo-500 bg-indigo-500/10"
                                : "border-gray-300 dark:border-gray-700 hover:border-indigo-500/50"
                            }`}
                          >
                            <p className="font-bold">{r.label}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{r.desc}</p>
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                </Card>

                <Card>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field
                      label="Time horizon (years)"
                      hint="How long until you'll need this money"
                    >
                      <Input
                        type="number"
                        min="1"
                        max="50"
                        value={profile.time_horizon ?? ""}
                        onChange={(e) =>
                          setProfile({
                            ...profile,
                            time_horizon: e.target.value ? Number(e.target.value) : null,
                          })
                        }
                        onBlur={() =>
                          patch({ time_horizon: profile.time_horizon ?? null })
                        }
                      />
                    </Field>
                    <Field
                      label="Tax jurisdiction"
                      hint="Drives tax-aware advice (Tally agent)"
                    >
                      <Select
                        value={profile.tax_jurisdiction || ""}
                        onChange={(e) => {
                          setProfile({ ...profile, tax_jurisdiction: e.target.value });
                          patch({ tax_jurisdiction: e.target.value });
                        }}
                      >
                        {TAX_JURISDICTIONS.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>
                </Card>

                <Card>
                  <Field label="Investment style" hint="Lens the Council prefers when discussing strategy">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {STYLE_OPTIONS.filter((o) => o.value).map((s) => {
                        const active = profile.investment_style === s.value;
                        return (
                          <button
                            key={s.value}
                            onClick={() => {
                              setProfile({ ...profile, investment_style: s.value });
                              patch({ investment_style: s.value });
                            }}
                            className={`text-left p-3 rounded-lg border transition ${
                              active
                                ? "border-indigo-500 bg-indigo-500/10"
                                : "border-gray-300 dark:border-gray-700 hover:border-indigo-500/50"
                            }`}
                          >
                            <p className="font-bold text-sm">{s.label}</p>
                            <p className="text-[11px] text-gray-500 mt-0.5">{s.desc}</p>
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                </Card>

                <Card>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-gray-200 dark:border-white/10">
                      <div>
                        <p className="font-semibold text-sm">Income focus</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          This money needs to produce income (dividends / interest)
                        </p>
                      </div>
                      <Toggle
                        checked={!!profile.income_focus}
                        onChange={(v) => {
                          setProfile({ ...profile, income_focus: v });
                          patch({ income_focus: v });
                        }}
                      />
                    </div>
                    <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-gray-200 dark:border-white/10">
                      <div>
                        <p className="font-semibold text-sm">ESG / sustainable preference</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          Favour ESG-aligned options when comparable
                        </p>
                      </div>
                      <Toggle
                        checked={!!profile.esg_preference}
                        onChange={(v) => {
                          setProfile({ ...profile, esg_preference: v });
                          patch({ esg_preference: v });
                        }}
                      />
                    </div>
                  </div>
                </Card>

                <Card>
                  <Field label="Liquidity needs" hint="How quickly might you need access to this money?">
                    <Select
                      value={profile.liquidity_needs || ""}
                      onChange={(e) => {
                        setProfile({ ...profile, liquidity_needs: e.target.value });
                        patch({ liquidity_needs: e.target.value });
                      }}
                    >
                      {LIQUIDITY_OPTIONS.map((l) => (
                        <option key={l.value} value={l.value}>
                          {l.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </Card>

                <Card>
                  <Field label="Exclusions" hint="Sectors or themes the Council should never recommend">
                    <div className="flex flex-wrap gap-2">
                      {SECTOR_EXCLUSIONS.map((s) => {
                        const list = profile.excluded_sectors || [];
                        const active = list.includes(s);
                        const next = active
                          ? list.filter((x) => x !== s)
                          : [...list, s];
                        return (
                          <button
                            key={s}
                            onClick={() => {
                              setProfile({ ...profile, excluded_sectors: next });
                              patch({ excluded_sectors: next });
                            }}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                              active
                                ? "border-red-500 bg-red-500/10 text-red-500"
                                : "border-gray-300 dark:border-gray-700 hover:border-red-500/50 text-gray-700 dark:text-gray-300"
                            }`}
                          >
                            {active ? "✕ " : "+ "}
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                </Card>

                <Card>
                  <Field
                    label="Personal investing notes"
                    hint="Anything the Council should know about you that the form fields don't capture. Free text. Always read by every agent."
                  >
                    <textarea
                      rows={4}
                      value={profile.investment_notes || ""}
                      onChange={(e) =>
                        setProfile({ ...profile, investment_notes: e.target.value })
                      }
                      onBlur={() =>
                        patch({ investment_notes: profile.investment_notes || "" })
                      }
                      placeholder='e.g. "I run my own business, my income is lumpy. I already have 12 months of cash. I want to focus on long-term wealth, not short-term gains. I avoid leveraged products."'
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </Field>
                </Card>

                <Card>
                  <h3 className="font-bold mb-1">Per-agent notes (optional)</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Give a specific Council member context only they should see. Useful for
                    detail one specialist needs that the others don't.
                  </p>

                  <div className="space-y-4">
                    {[
                      {
                        key: "tally_notes",
                        agent: "Tally",
                        accent: "text-amber-500",
                        placeholder:
                          'e.g. "I\'m on India\'s new tax regime — no 80C deductions. I have an HUF account I file separately. I sold equity last quarter for a ₹2L STCG."',
                      },
                      {
                        key: "vigil_notes",
                        agent: "Vigil",
                        accent: "text-rose-500",
                        placeholder:
                          'e.g. "I have a 6-month emergency fund separate from this portfolio. I never panic-sell. Don\'t flag normal corrections — only > 20% drawdowns."',
                      },
                      {
                        key: "compass_notes",
                        agent: "Compass",
                        accent: "text-emerald-500",
                        placeholder:
                          'e.g. "Target allocation 60/30/10 equity/debt/gold. Rebalance only when drift > 5%. Avoid leveraged products."',
                      },
                      {
                        key: "lens_notes",
                        agent: "Lens",
                        accent: "text-sky-500",
                        placeholder:
                          'e.g. "When valuing a stock, prioritise free cash flow yield and ROIC over P/E. Always check insider ownership."',
                      },
                    ].map((row) => (
                      <div key={row.key}>
                        <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1.5">
                          <span className={`text-sm font-bold ${row.accent}`}>
                            {row.agent}
                          </span>
                          <span>· notes</span>
                        </label>
                        <textarea
                          rows={3}
                          value={profile[row.key] || ""}
                          onChange={(e) =>
                            setProfile({ ...profile, [row.key]: e.target.value })
                          }
                          onBlur={() => patch({ [row.key]: profile[row.key] || "" })}
                          placeholder={row.placeholder}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </Card>

                <Card>
                  <p className="text-xs text-gray-500">
                    Goal-based planning (retirement, house, education, FIRE) is the next bit
                    on the roadmap — your goals will live here.
                  </p>
                </Card>
              </SectionShell>
            )}

            {active === "goals" && (
              <SectionShell
                title="Goals"
                description="What you're investing for. Compass uses these to project whether your portfolio will get you there in time."
              >
                <Card>
                  <GoalsEditor profile={profile} setProfile={setProfile} patch={patch} />
                </Card>
              </SectionShell>
            )}

            {active === "notifications" && (
              <SectionShell
                title="Notifications"
                description="Pick exactly what reaches you and how."
              >
                <Card>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                          <th className="text-left py-2 pr-4">Category</th>
                          {NOTIF_CHANNELS.map((c) => (
                            <th key={c} className="py-2 px-2 capitalize">
                              {c}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {NOTIF_CATEGORIES.map((cat) => (
                          <tr
                            key={cat.key}
                            className="border-t border-gray-200 dark:border-white/5"
                          >
                            <td className="py-3 pr-4">
                              <p className="font-semibold">{cat.label}</p>
                              <p className="text-xs text-gray-500">{cat.desc}</p>
                            </td>
                            {NOTIF_CHANNELS.map((ch) => (
                              <td key={ch} className="py-3 px-2 text-center">
                                <Toggle
                                  checked={!!notifPrefs[cat.key]?.[ch]}
                                  onChange={(v) => updateNotif(cat.key, ch, v)}
                                  disabled={ch === "sms" && !profile.phone}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!profile.phone && (
                    <p className="text-[11px] text-amber-500 mt-3">
                      Add a phone number on the Account tab to enable SMS notifications.
                    </p>
                  )}
                </Card>

                <Card>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Field label="Daily brief delivery time">
                      <Input
                        type="time"
                        value={notifPrefs.brief_time || "07:00"}
                        onChange={(e) => updateNotifMeta("brief_time", e.target.value)}
                      />
                    </Field>
                    <Field label="Quiet hours start">
                      <Input
                        type="time"
                        value={notifPrefs.quiet_hours?.start || "22:00"}
                        onChange={(e) =>
                          updateNotifMeta("quiet_hours", {
                            ...(notifPrefs.quiet_hours || {}),
                            start: e.target.value,
                          })
                        }
                      />
                    </Field>
                    <Field label="Quiet hours end">
                      <Input
                        type="time"
                        value={notifPrefs.quiet_hours?.end || "07:00"}
                        onChange={(e) =>
                          updateNotifMeta("quiet_hours", {
                            ...(notifPrefs.quiet_hours || {}),
                            end: e.target.value,
                          })
                        }
                      />
                    </Field>
                  </div>
                </Card>
              </SectionShell>
            )}

            {active === "appearance" && (
              <SectionShell title="Appearance" description="Make FinAI look the way you want.">
                <Card>
                  <Field label="Theme">
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: "light", label: "Light" },
                        { value: "dark", label: "Dark" },
                        { value: "system", label: "System" },
                      ].map((t) => {
                        const active = theme === t.value;
                        return (
                          <button
                            key={t.value}
                            onClick={() => setTheme(t.value)}
                            className={`p-3 rounded-lg border font-semibold transition ${
                              active
                                ? "border-indigo-500 bg-indigo-500/10 text-indigo-500"
                                : "border-gray-300 dark:border-gray-700 hover:border-indigo-500/50"
                            }`}
                          >
                            {t.label}
                          </button>
                        );
                      })}
                    </div>
                  </Field>
                </Card>

                <Card>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Density" hint="How much breathing room cards get">
                      <Select
                        value={appearancePrefs.density}
                        onChange={(e) => updateAppearance("density", e.target.value)}
                      >
                        <option value="comfortable">Comfortable</option>
                        <option value="cozy">Cozy</option>
                        <option value="compact">Compact</option>
                      </Select>
                    </Field>
                    <Field label="Default landing page">
                      <Select
                        value={appearancePrefs.default_landing}
                        onChange={(e) =>
                          updateAppearance("default_landing", e.target.value)
                        }
                      >
                        <option value="/portfolio">Portfolio overview</option>
                        <option value="/portfolio/holdings">Holdings</option>
                        <option value="/portfolio/insights">Insights</option>
                        <option value="/community">Community</option>
                      </Select>
                    </Field>
                  </div>
                </Card>
              </SectionShell>
            )}

            {active === "privacy" && (
              <SectionShell
                title="Privacy & data"
                description="Who sees what, and how to take your data with you."
              >
                <Card>
                  <Field label="Profile visibility">
                    <Select
                      value={profile.privacy_level || "public"}
                      onChange={(e) => {
                        setProfile({ ...profile, privacy_level: e.target.value });
                        patch({ privacy_level: e.target.value });
                      }}
                    >
                      <option value="public">Public — anyone can find me</option>
                      <option value="friends">Friends only</option>
                      <option value="private">Private — invisible on leaderboards</option>
                    </Select>
                  </Field>
                </Card>

                <Card>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm">Appear in AI Matchmaking</p>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        Let FinAI suggest you to other investors with similar (or opposite)
                        strategies, based on broad sector allocations and investing style.
                        Off = you're never recommended to anyone and your matchmaking row is hidden.
                      </p>
                    </div>
                    <Toggle
                      checked={!profile.match_opt_out}
                      onChange={(v) => {
                        setProfile({ ...profile, match_opt_out: !v });
                        patch({ match_opt_out: !v });
                      }}
                    />
                  </div>
                </Card>

                <Card>
                  <ShareSection profile={profile} setProfile={setProfile} patch={patch} flash={flash} />
                </Card>

                <Card>
                  <h3 className="font-bold mb-2 flex items-center gap-2">
                    Export your data
                    {!meetsFeature(profile?.tier, "data_export") && (
                      <TierBadge tier={featureMinTier("data_export")} />
                    )}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Download a JSON snapshot of your profile and account counts. Full export
                    (positions, conversations, insights) is coming soon.
                  </p>
                  <button
                    onClick={onExport}
                    className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold"
                  >
                    Download snapshot
                  </button>
                </Card>
              </SectionShell>
            )}

            {active === "connected" && (
              <SectionShell
                title="Connected accounts"
                description="Brokerages, wallets, and social logins."
              >
                {/* LinkedAccountsPanel = the actual brokerage + Web3-wallet
                    management UI, rendered inline. Used to live at
                    /portfolio/accounts with its own page chrome; folded here
                    so Settings is the single source of truth. */}
                <LinkedAccountsPanel />

                <Card>
                  <h3 className="font-bold">Social sign-in</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Google sign-in is wired. Apple, GitHub, and others coming soon.
                  </p>
                  <span className="inline-block mt-2 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-500">
                    Coming soon
                  </span>
                </Card>
              </SectionShell>
            )}

            {active === "billing" && (
              <SectionShell title="Subscription" description="Your plan and billing.">
                <Card>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-gray-500">
                        Current plan
                      </p>
                      <h3 className="text-2xl font-bold capitalize">
                        FinAI {profile?.tier || "Basic"}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Compare tiers and switch anytime — no payment required yet.
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-500">
                        Active
                      </span>
                      <Link
                        to="/pricing"
                        className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 whitespace-nowrap"
                      >
                        Manage plan
                      </Link>
                    </div>
                  </div>
                </Card>

                {/* Advisor-plan subscriptions the user has taken (Phase 3).
                    Stacked card pattern — a user can hold many concurrently. */}
                <MySubscriptionsPanel />

                {/* Advisor marketplace entry point (Phase 1).
                    If the user already applied, swap the CTA for a link to
                    the (Phase 2) advisor dashboard. */}
                <Card>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-xs uppercase tracking-widest text-gray-500">
                        Advisor marketplace
                      </p>
                      <h3 className="text-xl font-bold mt-0.5">Become an Advisor</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-prose">
                        Publish strategies, earn from subscribers, stay anonymous. Your subscribers
                        see your qualifications and intro — never their identity reaches you.
                      </p>
                    </div>
                    {profile?.is_advisor ? (
                      <Link
                        to="/advisor"
                        className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 whitespace-nowrap"
                      >
                        Open dashboard →
                      </Link>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setAdvisorModalOpen(true)}
                        className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-500 whitespace-nowrap"
                      >
                        Apply
                      </button>
                    )}
                  </div>
                  {profile?.is_advisor && (
                    <p className="mt-3 text-xs text-emerald-600 dark:text-emerald-400 font-semibold uppercase tracking-widest">
                      You're an advisor — set up your profile from the dashboard.
                    </p>
                  )}
                </Card>
              </SectionShell>
            )}

            {active === "help" && (
              <SectionShell
                title="Help & legal"
                description="Resources and the boring-but-important."
              >
                <Card>
                  <ul className="divide-y divide-gray-200 dark:divide-white/5">
                    {[
                      { label: "Help center", href: "#" },
                      { label: "Contact support", href: "mailto:support@finai.app" },
                      { label: "Terms of Service", href: "#" },
                      { label: "Privacy Policy", href: "#" },
                      { label: "Open-source acknowledgments", href: "#" },
                    ].map((link) => (
                      <li key={link.label} className="py-3">
                        <a
                          href={link.href}
                          className="font-semibold text-indigo-500 hover:underline"
                        >
                          {link.label} →
                        </a>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-gray-500 mt-4">
                    FinAI v0.3 · Council ships with Anchor, Compass, Lens. Vigil and Tally are next.
                  </p>
                </Card>
              </SectionShell>
            )}
          </main>
        </div>
      </div>

      <BecomeAdvisorModal
        open={advisorModalOpen}
        onClose={() => setAdvisorModalOpen(false)}
        onSuccess={() => {
          setAdvisorModalOpen(false);
          // Two state surfaces need to reflect the flip:
          // 1) Local Settings profile — drives this page's card UI.
          // 2) Global AuthContext profile — drives AdvisorShell's role guard
          //    and the Navbar dropdown's "Advisor dashboard" entry. Without
          //    refreshProfile() here, clicking "Open dashboard →" lands on
          //    /advisor with a stale cached is_advisor=false and bounces
          //    right back to /settings.
          setProfile((p) => (p ? { ...p, is_advisor: true } : p));
          refreshProfile();
          flash("success", "You're an advisor. Open the dashboard to set up your profile.");
        }}
      />
    </div>
  );
};

export default Settings;
